import { useState } from "react";
import { createContext } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../../redux/actions/loadingActions/loadingActions";
import {
  chargeWalletUserService,
  getTransactionsUserService,
} from "./../../../services/userServices";
import {
  deliverySuccessMessage,
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";
import {
  createTransactionAdminService,
  editTransactionAdminService,
  getUsersAdminService,
} from "../../../services/adminServices";
import { setUserAction } from "../../../redux/actions/userActions/userActions";

export const transactionUserContext = createContext({
  transactions: [],
  setTransactions: () => {},
  transaction: {},
  setTransaction: () => {},
  filter: "",
  setFilter: () => {},
  sort: "",
  setSort: () => {},
  start: "",
  setStart: () => {},
  end: "",
  setEnd: () => {},
  type: "",
  setType: () => {},
  pageCount: null,
  setPageCount: () => {},
  pageNumber: null,
  setPageNumber: () => {},
  itemsCount: null,
  setItemsCount: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  isShowChargeModal: null,
  setIsShowChargeModal: () => {},
  handleShowModal: () => {},
  handleHideModal: () => {},
  handleGetUsers: () => {},
  handleGetTransactions: () => {},
  handleCreateTransaction: () => {},
  handleEditTransaction: () => {},
  handleChargeWallet: () => {},
});

const TransactionUserContext = ({ children }) => {
  const dispatch = useDispatch();

  const { user } = useParams();

  const [transactions, setTransactions] = useState([]);
  const [transaction, setTransaction] = useState({});
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [type, setType] = useState("");
  const [pageCount, setPageCount] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsCount, setItemsCount] = useState(100);
  const [isShowModal, setIsShowModal] = useState(false);
  const [isShowChargeModal, setIsShowChargeModal] = useState(false);

  // Modal Operation
  const handleShowModal = (val, setValue) => {
    setIsShowModal(true);

    if (val) {
      setTransaction({ ...val, isEdit: true });
      setValue("documents", val.documents);
      setValue("description", val.description);
    }
  };
  const handleHideModal = () => {
    setIsShowModal(false);
    setTransaction({});
  };

  // Get Users
  const handleGetUsers = async (filter, callback) => {
    const objData = {
      filter,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getUsersAdminService(objData);
      if (status === 200) {
        !!callback &&
          callback(
            data.result?.users?.map((item) => ({
              value: item._id,
              label: `${item.firstName} ${item.lastName} - ${item.phoneNumber}`,
            }))
          );
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Get Transactions
  const handleGetTransactions = async (event) => {
    event?.preventDefault();

    const objData = {
      ...(start && { start }),
      ...(end && { end }),
      ...(user && { user }),
      filter,
      pageNumber,
      sort,
      itemsCount,
      type,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getTransactionsUserService(objData);
      if (status === 200) {
        setTransactions(data.result?.transactions);
        setPageCount(data?.result?.allPagesCount);
        dispatch(setUserAction());
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Create Transaction
  const handleCreateTransaction = async (userData) => {
    const objData = {
      ...userData,
      user: userData?.user?.value,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await createTransactionAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetTransactions();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Edit Transaction
  const handleEditTransaction = async (userData) => {
    const objData = {
      id: transaction._id,
      ...userData,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await editTransactionAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetTransactions();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Charge Wallet
  const handleChargeWallet = async (userData) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await chargeWalletUserService(userData);
      if (status === 200) {
        deliverySuccessMessage(data.resultMessage);
        window.location.href = data.result.redirectUrl;
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <transactionUserContext.Provider
      value={{
        transactions,
        setTransactions,
        transaction,
        setTransaction,
        filter,
        setFilter,
        sort,
        setSort,
        start,
        setStart,
        end,
        setEnd,
        type,
        setType,
        pageCount,
        setPageCount,
        pageNumber,
        setPageNumber,
        itemsCount,
        setItemsCount,
        isShowModal,
        setIsShowModal,
        isShowChargeModal,
        setIsShowChargeModal,
        handleShowModal,
        handleHideModal,
        handleGetUsers,
        handleGetTransactions,
        handleCreateTransaction,
        handleEditTransaction,
        handleChargeWallet,
      }}
    >
      {children}
    </transactionUserContext.Provider>
  );
};

export default TransactionUserContext;
